import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import ReactModal from 'react-modal';
import PropTypes from 'prop-types';

import { REQUEST_CANCEL_REASONS } from '../../../constants/general.constants';
import { errorHandler } from '../../services/authService';
import { cancelRequest } from '../../services/requests';
import { required } from '../../utils/form-default-errors';
import { Button, IconButton, Select, TextArea, Typography } from '..';
import * as S from './CancelRequestModal.styles';

export const CancelRequestModal = forwardRef(({ role }, ref) => {
	const [isOpen, setIsOpen] = useState(false);
	const history = useHistory();

	const {
		handleSubmit,
		control,
		register,
		setValue,
		reset,
		formState: { errors, isSubmitting },
	} = useForm();

	const handleOpen = (requestId) => {
		setValue('requestId', requestId);
		setIsOpen(true);
	};

	const handleClose = () => {
		setIsOpen(false);
		setTimeout(() => {
			reset();
		}, 200);
	};

	async function asyncCaller(data) {
		await cancelRequest(data);
		history.push(`/${role || 'teacher'}/requests`);
	}

	const onSubmit = (e) => {
		handleSubmit(asyncCaller)(e).catch(errorHandler);
	};

	useImperativeHandle(
		ref,
		() => ({
			open: handleOpen,
			close: handleClose,
		}),
		[handleOpen, handleClose]
	);

	return (
		<ReactModal className="smaller-modal" ref={ref} closeTimeoutMS={200} isOpen={isOpen} onRequestClose={handleClose}>
			<S.Form onSubmit={onSubmit}>
				<IconButton className="close-button" type="button" icon={['fal', 'times']} onClick={handleClose} />
				<S.InfoWrapper>
					<Typography tag="h2" weight="extrablack" center>
						Cancel Request
					</Typography>
					<Typography tag="p" center>
						Are you sure you want to cancel this request? Cancelling the request will make it inactive.
					</Typography>
				</S.InfoWrapper>
				<S.FieldsWrapper>
					<Controller
						name="cancelReason"
						control={control}
						rules={{ required: required('Reason') }}
						render={({ field: { onChange, value } }) => (
							<Select label="Reason" placeholder="Type to search" id="cancel-reason" error={errors?.cancelReason} options={REQUEST_CANCEL_REASONS} value={REQUEST_CANCEL_REASONS.find((c) => c.value === value) || ''} onChange={(val) => onChange(val.value)} />
						)}
					/>
					<TextArea label="Notes" placeholder="Add notes here" id="cancel-notes" error={errors?.cancelNotes} {...register('cancelNotes')} />
				</S.FieldsWrapper>
				<S.ButtonsWrapper>
					<Button type="button" variant="outline" onClick={handleClose}>
						<Typography weight="bold" variation="button-medium">
							Back
						</Typography>
					</Button>
					<Button type="submit">
						<Typography weight="bold" variation="button-medium">
							{isSubmitting ? 'Loading' : 'Cancel'}
						</Typography>
					</Button>
				</S.ButtonsWrapper>
			</S.Form>
		</ReactModal>
	);
});

CancelRequestModal.propTypes = {
	role: PropTypes.string,
};

CancelRequestModal.displayName = 'CancelRequestModal';
